/**
 * The mode switch — the way from one of Nebari's two views to the other.
 *
 * A button, not a menu. There are exactly two modes, so a dropdown asked the
 * viewer to open a list to choose the only other item on it. The button names
 * where it goes rather than where you are, which is also what makes it
 * readable without opening anything.
 *
 * Renders nothing for a viewer entitled to one mode, which is most people.
 *
 * Top bar only. Below `navtab` there is no top bar, and the switch becomes a
 * row in the drawer's account block instead — `ModeSwitchItem`.
 *
 * It wears the search field's chrome deliberately: the two sit side by side in
 * the same bar, and the mode switch is the same kind of thing — a control on
 * the chrome rather than a destination in it.
 *
 * Switching navigates to the mode's home and lets `useAppMode` re-derive the
 * mode from the URL, so the store is written by that derivation and there is no
 * second place here that could disagree with where the viewer ended up.
 */
import { ArrowLeftRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';
import { useTheme } from '../../hooks/useTheme';

import { modeHomeFor } from './mode-home';
import { NAV_THEMES } from './nav-tokens';
import type { AppModeInfo } from './use-app-mode';
import { modeLabel } from './viewMode';

export interface ModeSwitchProps {
  viewer: AppModeInfo;
}

export default function ModeSwitch({ viewer }: ModeSwitchProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);

  // The one mode that is not the current one. With two modes there is never
  // more than one candidate, and with fewer there is nothing to switch to.
  const target = viewer.availableModes.find((m) => m !== viewer.mode);
  if (!target) return null;

  const label = `${modeLabel(target)} mode`;

  return (
    <button
      type="button"
      data-testid="mode-switch"
      onClick={() => navigate(modeHomeFor(target, hasAnyPermission))}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={`Switch to ${label}`}
      title={`Switch to ${label}`}
      className="inline-flex h-[34px] shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 text-[13.5px] font-medium transition-colors"
      style={{
        color: hover ? t.ink : t.muted,
        background: hover ? t.bg : t.surface,
        border: `1px solid ${hover ? t.subtle : t.border}`,
      }}
    >
      <ArrowLeftRight size={16} aria-hidden className="shrink-0" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}
